import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Crown, Search, UserPlus } from "lucide-react";
import { useMe } from "@/context/MeContext";
import { useApi, useFetch, errMsg } from "@/lib/api";
import { Card, ConfirmDelete, ErrorBox, FormSheet, Loading, PageTitle } from "@/components/common";
import { Field, OptionSelect } from "@/components/fields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const ROLE_LABEL = { admin: "Rappresentante", alunno: "Alunno" };

function ClassForm() {
  const api = useApi();
  const { me, reload } = useMe();
  const [form, setForm] = useState(me.class_info);
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api("put", "/class", form); await reload(); toast.success("Dati classe salvati"); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Card>
      <h2 className="mb-3 font-heading text-lg font-bold">Dati della classe</h2>
      <form onSubmit={save} className="space-y-3" data-testid="class-form">
        <Field label="Nome classe"><Input className="h-11" value={form.name} placeholder="Es. 4ªB" onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="class-name-input" /></Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Scuola"><Input className="h-11" value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} data-testid="class-school-input" /></Field>
          <Field label="Anno scolastico"><Input className="h-11" value={form.year} placeholder="2026/2027" onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="class-year-input" /></Field>
        </div>
        <Button type="submit" disabled={busy || !form.name?.trim()} className="rounded-full" data-testid="class-save-btn">Salva</Button>
      </form>
    </Card>
  );
}

function AddStudentSheet({ open, onOpenChange, roles, onSaved }) {
  const api = useApi();
  const blank = { name: "", email: "", password: "", role: "alunno" };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(blank); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("La password deve avere almeno 8 caratteri");
    setBusy(true);
    try { await api("post", "/users", form); toast.success("Alunno aggiunto"); onOpenChange(false); onSaved(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <FormSheet open={open} onOpenChange={onOpenChange} title="Aggiungi alunno" description="Comunica email e password all'alunno: potrà accedere anche con Google se usa la stessa email.">
      <form onSubmit={save} className="space-y-4" data-testid="add-student-form">
        <Field label="Nome e cognome"><Input className="h-11" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="add-student-name" /></Field>
        <Field label="Email"><Input type="email" inputMode="email" autoComplete="off" required className="h-11" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="add-student-email" /></Field>
        <Field label="Password temporanea" hint="Almeno 8 caratteri"><Input type="text" autoComplete="new-password" required className="h-11" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="add-student-password" /></Field>
        <Field label="Ruolo"><OptionSelect value={form.role} onChange={(v) => setForm({ ...form, role: v })} options={roles.map((r) => ({ value: r, label: ROLE_LABEL[r] || r }))} testId="add-student-role" /></Field>
        <Button type="submit" disabled={busy} className="h-12 w-full rounded-full text-base" data-testid="add-student-submit">{busy ? "Creazione…" : "Crea account"}</Button>
      </form>
    </FormSheet>
  );
}

function StudentRow({ u, roles, isMe, onRole, onRemove }) {
  return (
    <div className="flex items-center gap-3 rounded-xl p-2.5" data-testid={`student-row-${u.id}`}>
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-secondary font-heading font-bold">{u.name?.[0]?.toUpperCase()}</span>
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 truncate font-semibold">{u.name}{u.role === "admin" && <Crown className="h-3.5 w-3.5 text-amber-500" />}{isMe && <span className="text-xs font-normal text-muted-foreground">(tu)</span>}</p>
        <p className="truncate text-xs text-muted-foreground">{u.email}{!u.active && " · non ha ancora effettuato l'accesso"}</p>
      </div>
      <div className="w-36 shrink-0">
        <OptionSelect value={u.role} onChange={(r) => onRole(u, r)} options={roles.map((r) => ({ value: r, label: ROLE_LABEL[r] || r }))} testId={`student-role-${u.id}`} />
      </div>
      {!isMe && <ConfirmDelete testId={`student-remove-${u.id}`} label="Rimuovi" title={`Rimuovere ${u.name} dalla classe?`} onConfirm={onRemove} />}
    </div>
  );
}

export default function GestioneClasse() {
  const api = useApi();
  const navigate = useNavigate();
  const { me } = useMe();
  const { data, error, reload } = useFetch("/users");
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  if (error) return <ErrorBox text={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const list = data.users.filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase()));
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4 lg:max-w-3xl">
      <button onClick={() => navigate("/altro")} className="flex min-h-11 items-center gap-1 text-sm font-semibold text-muted-foreground" data-testid="classe-back-btn"><ArrowLeft className="h-4 w-4" />Impostazioni</button>
      <PageTitle title="Gestione classe" subtitle={`${data.users.length} membri`}
        action={<Button className="rounded-full" onClick={() => setAdding(true)} data-testid="btn-add-student-rep-admin"><UserPlus className="mr-1.5 h-4 w-4" />Aggiungi</Button>} />
      <ClassForm />
      <Card className="p-2">
        <div className="relative m-1 mb-2">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="h-11 pl-9" placeholder="Cerca alunno" value={q} onChange={(e) => setQ(e.target.value)} data-testid="students-search" />
        </div>
        <div className="divide-y divide-border/60">
          {list.map((u) => (
            <StudentRow key={u.id} u={u} roles={data.roles} isMe={u.id === me.id}
              onRole={(x, r) => act(() => api("patch", `/users/${x.id}/role`, { role: r }), "Ruolo aggiornato")}
              onRemove={() => act(() => api("delete", `/users/${u.id}`), "Rimosso dalla classe")} />
          ))}
        </div>
      </Card>
      <AddStudentSheet open={adding} onOpenChange={setAdding} roles={data.roles} onSaved={reload} />
    </div>
  );
}
